"use client";
import React, { useMemo, useState } from "react";
import Masonry from "react-masonry-css";
import StyleToggle from "./styleToggle";
import { useGlobalContext } from "../../../app/context/store";
import { generators } from "../../shared/generators/generators";
import { locales } from "../../../i18n";
import "./pictureCard/style.css";
import { Accordion, AccordionHeader, AccordionBody } from "@material-tailwind/react";
import { useGenerator } from "@/app/context/GeneratorContext";
import GemCost from "../generatorViewPort/gemCost";

const breakpointColumnsObj = {
  default: 4,
  1124: 3,
  769: 3,
  500: 3,
};

type LangIndices = { [key: string]: number };

const createLangIndices = (locales: readonly string[]): LangIndices => {
  const indices: LangIndices = {};
  locales.forEach((locale, index) => {
    indices[locale] = index; // Directly using the locale string as the key
  });
  return indices;
};

const langIndices: LangIndices = createLangIndices(locales);

const getLocaleIndex = (locale: string): number => {
  return langIndices[locale] ?? 0; // Directly use the locale string to get the index
};

export default function StyleToggleSet({ translations }: { translations: any }) {
  const { user } = useGlobalContext();
  const { state, calculateCreditCost, batchSize, generatorList, generatorListSuccess } =
    useGenerator();
  const [open, setOpen] = useState(0);
  const handleOpen = (value: number) => {
    setOpen(open === value ? 0 : value);
  };

  const creditCost = useMemo(
    () => calculateCreditCost(),
    [state.creditCost?.premiumCost, state.creditCost?.generatorCost, batchSize, state.queueType],
  );

  return (
    <div className="mb-4 shadow-sm sm:mb-0">
      <Accordion
        open={open === 1}
        icon={<Icon id={1} open={open} />}
        className={`border-[0.5px] border-[#3C3C3C] bg-[#2B2D31] px-2 py-2 md:px-4 ${
          user._id === "none" ? "sm:rounded-tl-lg sm:rounded-tr-lg" : ""
        }`}
      >
        <AccordionHeader
          onClick={() => handleOpen(1)}
          className={`border-b-0 px-[12px] py-2 text-sm text-white sm:px-0 ${
            open === 1 ? "text-white hover:!text-white" : ""
          }`}
        >
          {"" + //"✦ "
            translations.style +
            ": " +
            (state.selectedGenerator &&
            state.selectedGenerator.names &&
            state.selectedGenerator.names.length > 0
              ? state.selectedGenerator.names[Number(translations.index)].value
              : "")}
          {user?.membership?.level !== -1 && (
            <div className="ml-auto flex flex-row">
              <GemCost amount={creditCost / batchSize} multiplier={batchSize} />
            </div>
          )}
        </AccordionHeader>
        <AccordionBody>
          <div className="grid grid-cols-3 gap-x-3 overflow-auto md:grid-cols-4">
            {generatorListSuccess && (
              <>
                {generatorList.map((generator, i) => (
                  <StyleToggle key={i} generator={generator} translations={translations} />
                ))}
              </>
            )}
          </div>
          <div className="mb-[-4px] mt-2 border-t-[1px] border-[#3C3C3C] pt-4 text-center capitalize text-white">
            {translations.explainX}
          </div>
        </AccordionBody>
      </Accordion>
    </div>
  );
}

function Icon({ id, open }: { id: number; open: number }) {
  // SVG Icon component logic
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}
